/**
 * Pipeline Dashboard Hook
 * Business logic for pipeline stats and task list
 */
import { useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useQueryWithHandler } from '@/hooks/useQueryWithHandler'
import useUrlSearchParams from '@/hooks/useUrlSearchParams'
import { useMutationWithHandler } from '@/hooks/useMutationWithHandler'
import { QUERY_KEYS } from '@/constants/reactQueryKeys'
import { getPipelineStats, getPipelineTasks, retryTask } from '../services'
import type { TaskWithCustomer } from '../types/response'
import {
    getPipelineColumns,
    getPipelineActions,
} from '../utils/report-pipeline-config'

export const usePipeline = () => {
    const { t } = useTranslation('report_waf')

    const { params, setParams } = useUrlSearchParams({
        defaults: { page: 1, page_size: 20 },
    })

    // Stats with auto-refresh
    const {
        data: stats,
        isLoading: statsLoading,
        refetch: refetchStats,
    } = useQueryWithHandler({
        queryKey: [QUERY_KEYS.PIPELINE_STATS],
        queryFn: getPipelineStats,
        refetchInterval: 10000,
    })

    // Tasks
    const {
        data: tasksData,
        renderState,
        refetch: refetchTasks,
    } = useQueryWithHandler({
        queryKey: [QUERY_KEYS.REPORT_WAF, 'pipeline-tasks', params],
        queryFn: () =>
            getPipelineTasks({
                page: params.page,
                page_size: params.page_size,
                q: '',
            }),
        placeholderData: true,
    })

    const tasks = tasksData?.results || []
    const pagination = tasksData?.pagination

    // Retry mutation
    const { mutateAsync: doRetry } = useMutationWithHandler(
        { mutationFn: (taskId: number) => retryTask(taskId) },
        {
            reactQueryKeys: QUERY_KEYS.REPORT_WAF,
            successMessage: t('pipeline.retry_success'),
            errorMessage: t('pipeline.retry_error'),
            onSuccess: () => {
                refetchTasks()
                refetchStats()
            },
        }
    )

    const handleRetry = useCallback(
        (row: TaskWithCustomer) => doRetry(row.id),
        [doRetry]
    )

    const refetchAll = useCallback(() => {
        refetchStats()
        refetchTasks()
    }, [refetchStats, refetchTasks])

    const columns = useMemo(() => getPipelineColumns(t), [t])
    const actions = useMemo(
        () => getPipelineActions(t, { onRetry: handleRetry }),
        [t, handleRetry]
    )

    const handlePageChange = useCallback(
        (page: number) => setParams({ page }, false),
        [setParams]
    )
    const handlePageSizeChange = useCallback(
        (size: number) => setParams({ page_size: size }),
        [setParams]
    )

    return {
        stats,
        statsLoading,
        tasks,
        pagination,
        params,
        stateComponent: renderState(),
        columns,
        actions,
        handlePageChange,
        handlePageSizeChange,
        refetchAll,
        t,
    }
}
